import React, { useState, useEffect } from 'react';
import styles from './paidPlanSubscription.module.css';
import PlanTimeline from '../PlanTimeline/PlanTimeline';
import { getTextStyles } from '../../../utils/textStyleUtils';
import logo from '../../../assets/images/verticaltv/Vertical by Zupee.svg';
import upiGroupImages from '../../../assets/upi_group_images.svg';

export interface InactivePaidPlanState {
  heroImageUrl?: string;
  bottomImageUrl?: string;
  upiButtonName?: string;
  socialProofing?: {
    bgGradientColors?: string;
    text: {
      parent: {
        text: string;
        style?: {
          textStyle?: string;
          textColor?: string;
        };
      };
      placeholder: {
        texts: string[];
        style?: {
          textStyle?: string;
          textGradientColors?: string;
        };
      };
    };
  };
  planData?: {
    title?: {
      text: string;
      style?: {
        textStyle?: string;
        textColor?: string;
      };
    };
    planBorderColor?: string;
    planBgColor?: string;
    selectedPlanColor?: string;
    plans?: Array<{
      id: string;
      validityValue: {
        text: string;
        style?: {
          textColor?: string;
          textStyle?: string;
        };
      };
      planValue: {
        text: string;
        style?: {
          textGradientColors?: string;
          textStyle?: string;
          gradientAngle?: number;
        };
      };
      strikePrice: {
        text: string;
        style?: {
          textStyle?: string;
          textColor?: string;
          isStrikeThrough?: boolean;
        };
      };
      actualPriceAmount: number;
      actualPrice: {
        text: string;
        style?: {
          textGradientColors?: string;
          textColor?: string;
          textStyle?: string;
        };
      };
      isSelected: boolean;
      timeline: {
        startDate: string;
        endDate: string;
      };
      tag?: string;
    }>;
  };
  ctaText?: string;
  mandateData?: {
    showMandate?: boolean;
    enableText?: {
      text: string;
      style?: {
        textStyle?: string;
        textColor?: string;
      };
    };
    defaultAutopay?: boolean;
  };
  videoUrl?: string;
  videoThumbnailUrl?: string;
}

interface PaidPlanSubscriptionProps {
  planState: InactivePaidPlanState;
  phoneNumber?: string;
  upiButtonName?: string;
  onPlanSelect?: (planId: string) => void;
  onPayClick?: (planId: string, autopayEnabled: boolean) => void;
  onEnterUpiId?: () => void;
}

const PaidPlanSubscription: React.FC<PaidPlanSubscriptionProps> = ({
  planState,
  phoneNumber,
  onPlanSelect,
  onPayClick,
  onEnterUpiId,
  upiButtonName,
}) => {
  // Find the plan with minimum actualPriceAmount (total price, not per-day price)
  const getPlanWithMinimumValue = () => {
    const plans = planState.planData?.plans || [];
    if (plans.length === 0) return null;
    
    return plans.reduce((minPlan, currentPlan) => {
      const minAmount = minPlan.actualPriceAmount || Infinity;
      const currentAmount = currentPlan.actualPriceAmount || Infinity;
      return currentAmount < minAmount ? currentPlan : minPlan;
    });
  };

  const minimumValuePlan = getPlanWithMinimumValue();
  const [selectedPlanId, setSelectedPlanId] = useState<string>(
    minimumValuePlan?.id || ''
  );
  const [autopayEnabled] = useState<boolean>(
    planState.mandateData?.defaultAutopay ?? true
  );

  const handlePlanSelect = (planId: string) => {
    setSelectedPlanId(planId);
    if (onPlanSelect) {
      onPlanSelect(planId);
    }
  };

  const handlePayClick = () => {
    if (onPayClick && selectedPlanId) {
      onPayClick(selectedPlanId, autopayEnabled);
    }
  };

  const selectedPlan = planState.planData?.plans?.find(p => p.id === selectedPlanId);

  // Call onPlanSelect on mount with the default selected plan
  useEffect(() => {
    if (selectedPlanId && onPlanSelect) {
      onPlanSelect(selectedPlanId);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Calculate per day price
  const calculatePerDayPrice = () => {
    if (!selectedPlan) return '';
    const amount = selectedPlan.actualPriceAmount || 0;
    const validityText = selectedPlan.validityValue?.text || '';
    const daysMatch = validityText.match(/\d+/);
    const days = daysMatch ? parseInt(daysMatch[0], 10) : 90;
    const perDay = (amount / days).toFixed(1);
    return `₹${perDay}/day`;
  };

  return (
    <div className={styles.container}>
      {/* Background Glow Effect */}
      {/* <div className={styles.backgroundGlow}>
        <svg 
          xmlns="http://www.w3.org/2000/svg" 
          width="360" 
          height="684" 
          viewBox="0 0 360 684" 
          fill="none"
          className={styles.backgroundSvg}
        >
          <g filter="url(#filter0_fn_527_9064)">
            <circle cx="180" cy="92" r="158" fill="#4F5568"/>
          </g>
          <defs>
            <filter 
              id="filter0_fn_527_9064" 
              x="-412" 
              y="-500" 
              width="1184" 
              height="1184" 
              filterUnits="userSpaceOnUse" 
              colorInterpolationFilters="sRGB"
            >
              <feFlood floodOpacity="0" result="BackgroundImageFix"/>
              <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape"/>
              <feGaussianBlur stdDeviation="217" result="effect1_foregroundBlur_527_9064"/>
              <feTurbulence 
                type="fractalNoise" 
                baseFrequency="0.5 0.5" 
                stitchTiles="stitch" 
                numOctaves="3" 
                result="noise" 
                seed="2135"
              />
              <feComponentTransfer in="noise" result="coloredNoise1">
                <feFuncR type="linear" slope="2" intercept="-0.5"/>
                <feFuncG type="linear" slope="2" intercept="-0.5"/>
                <feFuncB type="linear" slope="2" intercept="-0.5"/>
                <feFuncA 
                  type="discrete" 
                  tableValues="1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 1 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0"
                />
              </feComponentTransfer>
              <feComposite 
                operator="in" 
                in2="effect1_foregroundBlur_527_9064" 
                in="coloredNoise1" 
                result="noise1Clipped"
              />
              <feComponentTransfer in="noise1Clipped" result="color1">
                <feFuncA type="table" tableValues="0 0.4"/>
              </feComponentTransfer>
              <feMerge result="effect2_noise_527_9064">
                <feMergeNode in="effect1_foregroundBlur_527_9064"/>
                <feMergeNode in="color1"/>
              </feMerge>
            </filter>
          </defs>
        </svg>
      </div> */}

      {/* Header Background Image */}
      {planState.bottomImageUrl && (
        <div className={styles.headerImageContainer}>
          <img src={planState.bottomImageUrl} alt="Header" className={styles.headerImage} />
        </div>
      )}

      {/* Logo - Centered */}
      <div className={styles.logoContainer}>
        <div className={styles.logoWrapper}>
          <img src={logo} alt="Vertical TV by Zupee" className={styles.logo} />
        </div>
      </div>

      {/* Main Content */}
      <div className={styles.content}>
        {/* Title */}
        <h2 className={styles.title}>
          Buy Subscription
        </h2>

        {/* Single Plan Card */}
        {selectedPlan && (
          <div className={styles.planCardWrapper}>
            <div className={styles.planCard}>
              <div className={styles.planHeader}>
                <div className={styles.priceContainer}>
                  <span
                    className={styles.actualPrice}
                    style={getTextStyles(selectedPlan.actualPrice.style)}
                  >
                    {selectedPlan.actualPrice.text}
                  </span>
                  {selectedPlan.strikePrice && (
                    <span
                      className={styles.strikePrice}
                      style={getTextStyles(selectedPlan.strikePrice.style)}
                    >
                      {selectedPlan.strikePrice.text}
                    </span>
                  )}
                </div>
                <div className={styles.perDayBadge}>
                  <span className={styles.perDayText}>
                    {calculatePerDayPrice()}
                  </span>
                </div>
              </div>
              <div className={styles.planFooter}>
                <span
                  className={styles.validityText}
                  style={getTextStyles(selectedPlan.validityValue.style)}
                >
                  {selectedPlan.validityValue.text}
                </span>
                <div className={styles.checkIcon}>
                  {/* <img 
                    src="https://www.figma.com/api/mcp/asset/b3bda687-2f18-4f3d-82d3-ccbb86b5e592" 
                    alt="Check" 
                    className={styles.checkIconImg}
                  /> */}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Timeline */}
        {selectedPlan && (
          <PlanTimeline
            startDate={selectedPlan.timeline.startDate}
            endDate={selectedPlan.timeline.endDate}
            phoneNumber={phoneNumber}
          />
        )}
      </div>

      {/* Social Proof and CTA Section */}
      <div className={styles.bottomSection}>
        {/* Social Proof */}
        <div className={styles.socialProof}>
          <div className={styles.socialProofContent}>
            <span className={styles.socialProofNumber}>500K+</span>
            <span className={styles.socialProofText}>Happy subscribers 🎉</span>
          </div>
        </div>

        {/* CTA Button */}
        {selectedPlan && (
          <>
            <div className={styles.ctaWrapper}>
              <button
                className={styles.ctaButton}
                onClick={handlePayClick}
                disabled={false}
              >
                <img src={upiGroupImages} alt="UPI apps" className={styles.upiGroupIcon} />
                <span>Pay using UPI apps</span>
              </button>
            </div>
            
            {/* Enter UPI ID Button */}
            {onEnterUpiId && (
              <div className={styles.ctaWrapper}>

                <button
                  className={styles.enterUpiButton}
                  onClick={onEnterUpiId}
                  disabled={false}
                >
                  <span>{upiButtonName}</span>
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default PaidPlanSubscription;

